import React, { useMemo } from 'react';
import { StyleSheet, View, Pressable, ScrollView, ActivityIndicator } from 'react-native';
import PropTypes from 'prop-types';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';
import {
  AGENT_MENTION_ROW_HEIGHT,
  AGENT_MENTION_MAX_VISIBLE_ROWS,
  getAgentMentionListHeight,
} from '@/components/ChatInput/agentMentionLayout';

/**
 * Agent picker shown above the native composer while an "@" mention is being
 * typed. The web composer gets this from react-mentions, which is DOM-only.
 */
export default function AgentMentionSuggestions({ suggestions, isSearching, onSelect }) {
  const { colors } = useTheme();
  const listHeight = getAgentMentionListHeight(suggestions.length);

  const styles = useMemo(() => StyleSheet.create({
    container: {
      marginHorizontal: 8,
      marginBottom: 6,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_primary,
      overflow: 'hidden',
      flexGrow: 0,
      flexShrink: 0,
      maxHeight: AGENT_MENTION_ROW_HEIGHT * AGENT_MENTION_MAX_VISIBLE_ROWS,
    },
    row: {
      minHeight: AGENT_MENTION_ROW_HEIGHT,
      paddingHorizontal: 12,
      paddingVertical: 10,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    rowPressed: {
      backgroundColor: colors.solid_color_secondary,
    },
    separator: {
      height: StyleSheet.hairlineWidth,
      backgroundColor: colors.border_color_secondary,
      marginHorizontal: 12,
    },
    empty: {
      paddingHorizontal: 12,
      paddingVertical: 12,
      flexDirection: 'row',
      alignItems: 'center',
    },
    spinner: {
      marginRight: 8,
    },
  }), [colors]);

  if (suggestions.length === 0) {
    if (!isSearching) return null;
    return (
      <View style={styles.container}>
        <View style={styles.empty}>
          <ActivityIndicator size="small" color={colors.text_color_secondary} style={styles.spinner} />
          <Text size="s" color="secondary">Searching agents…</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, { height: listHeight }]}>
      <ScrollView
        keyboardShouldPersistTaps="always"
        nestedScrollEnabled
        style={{ height: listHeight }}
      >
        {suggestions.map((agent, index) => (
          <View key={agent.id}>
            {index > 0 && <View style={styles.separator} />}
            <Pressable
              onPress={() => onSelect(agent)}
              style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
              accessibilityRole="button"
              accessibilityLabel={`Mention ${agent.name}`}
            >
              <Text size="n" color="primary" numberOfLines={1}>{agent.name}</Text>
              {agent.isPlusOnly && (
                <Text size="xs" color="secondary">Plus</Text>
              )}
            </Pressable>
          </View>
        ))}
      </ScrollView>
    </View>
  );
}

AgentMentionSuggestions.propTypes = {
  suggestions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    isPlusOnly: PropTypes.bool,
  })).isRequired,
  isSearching: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};
